import * as React from 'react';
import { connect } from 'react-redux';
import { HashRouter, Route, RouteComponentProps } from 'react-router-dom';

import { bindActionCreators } from 'redux';
import { hideRouterError } from '../../actions/spaces/RouterErrorActions';
import { usePrevious } from '../../hooks';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { AppState } from '../../state/AppState';
import { RouterErrorType } from '../../state/RouterErrorType';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { RouterError } from './RouterError';

interface ReduxRouterErrorWrapperOwnProps {
  readonly children: React.ReactNode;
}

interface ReduxRouterErrorWrapperStateProps {
  readonly displayError: boolean;
  readonly errorType: RouterErrorType;
  readonly redirectSiteUrlName: string;
  readonly urls: SpacesSiteUrls;
}

interface ReduxRouterErrorWrapperDispatchProps {
  readonly hideRouterError: () => void;
}

type ReduxRouterErrorWrapperProps = ReduxRouterErrorWrapperOwnProps & ReduxRouterErrorWrapperStateProps & ReduxRouterErrorWrapperDispatchProps;

const ReduxRouterErrorWrapper = React.memo((props: ReduxRouterErrorWrapperProps) => {
  const renderError = React.useCallback((routeProps: RouteComponentProps) => {
    const urls = props.redirectSiteUrlName ? new SpacesSiteUrls({siteUrlName: props.redirectSiteUrlName}) : props.urls;
    return <ReduxRouterError displayError={props.displayError} errorType={props.errorType} urls={urls} hide={props.hideRouterError} path={routeProps.location.pathname} />;
  }, [props.errorType]);

  if (props.displayError) {
    return (
      <HashRouter>
        <Route render={renderError}/>
      </HashRouter>
    );
  }

  return React.Children.only(props.children) as React.ReactElement<any>;
});

interface ReduxRouterErrorProps {
  readonly hide: () => void;
  readonly displayError: boolean;
  readonly errorType: RouterErrorType;
  readonly urls: SpacesSiteUrls;
  readonly path: string;
}

const ReduxRouterError = React.memo((props: ReduxRouterErrorProps) => {
  const previousPath = usePrevious(props.path);
  React.useEffect(() => {
    if (previousPath && previousPath !== props.path) {
      props.hide();
    }
  }, [props.path]);

  return <RouterError errorType={props.errorType} urls={props.urls} />;
});

const mapStateToProps: MapStateToProps<ReduxRouterErrorWrapperStateProps> = (state: AppState) => ({
  displayError: state.reduxRouterEror.displayError,
  errorType: state.reduxRouterEror.errorType,
  redirectSiteUrlName: state.reduxRouterEror.redirectSiteUrlName,
  urls: siteUrlsSelector(state),
});

const mapDispatchToProps: MapDispatchToProps<ReduxRouterErrorWrapperDispatchProps> = (dispatch) => bindActionCreators({
  hideRouterError: hideRouterError
}, dispatch);

export const ConnectedReduxRouterErrorWrapper = connect(mapStateToProps, mapDispatchToProps)(ReduxRouterErrorWrapper);
